import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/app/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/components/ui/card";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error captured by ErrorBoundary:", error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    localStorage.removeItem("assessmentEmail");
    localStorage.removeItem("assessmentProgress");
    this.setState({ hasError: false, error: undefined });
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
          <Card className="max-w-md w-full border-slate-800 bg-white">
            <CardHeader className="bg-slate-950">
              <CardTitle className="text-white text-xl">
                Une erreur s&apos;est produite
              </CardTitle>
              <CardDescription className="text-slate-200">
                L&apos;application a rencontré un problème inattendu.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {this.state.error && (
                <div className="text-sm text-slate-700 bg-slate-100 p-3 rounded-md">
                  <strong>Error details:</strong>
                  <br />
                  {this.state.error.message}
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={this.handleReload}
                  className="flex-1 bg-slate-950 hover:bg-slate-800 text-white"
                >
                  Recharger la page
                </Button>
                <Button
                  onClick={this.handleReset}
                  variant="outline"
                  className="flex-1"
                >
                  Réinitialiser
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
